import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import AppBrandMark from "../Branding/AppBrandMark";
import { sideMenuList } from "@Constant/constants";
import CustomArrowIcon from "../../assets/icons/SvgIcons/CustomArrowIcon";
import ChevronDownIcon from "../../assets/icons/SvgIcons/ChevronDownIcon";

const Sidebar = ({ onToggle, isSidebarCollapsed, isMobileTablet }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [hoverStart, setHoverStart] = useState(false);
  const sidebarRef = useRef(null);
  const state = location.state;
  const path = state?.path;
  const permissionsForRole = useSelector(
    (state) => state?.authSlice?.user?.user?.permissions,
  );

  const handleNavigation = (path) => {
    if (path) navigate(path);
  };

  const toggleSubMenu = (index) => {
    setExpandedMenus((prev) => ({
      [index]: !prev[index],
    }));
  };

  // Click outside to collapse (only on mobile/tablet)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileTablet &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        onToggle(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileTablet]);

  const isExpanded = !isSidebarCollapsed || hoverStart;

  const renderMenuItemContent = (item, isActive, isExpandedMenu) => (
    <>
      <div className="flex items-center gap-2 w-full min-w-0">
        <div
          className="sidebar-menu-icon w-5 h-5 shrink-0 flex items-center justify-center"
          dangerouslySetInnerHTML={{ __html: item.icon(isActive) }}
        />
        {isSidebarCollapsed && hoverStart && (
          <span
            className={`transition-all duration-300 select-none ${
              isActive ? "" : "text-white"
            }`}
          >
            {item.name}
          </span>
        )}
        {!isSidebarCollapsed && (
          <span
            className={`transition-all duration-300 select-none ${
              isActive ? "" : "text-white"
            }`}
          >
            {item.name}
          </span>
        )}
      </div>
      {item.subItems.length > 0 && (
        <ChevronDownIcon
          fill="white"
          className={`max-w-[10px] h-[6px] transition-transform duration-300 ease-in-out ${
            isSidebarCollapsed && !hoverStart ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
          } ${isExpandedMenu ? "rotate-180" : ""}`}
        />
      )}
    </>
  );

  return (
    <>
      {isMobileTablet ? (
        <button
          type="button"
          tabIndex={isSidebarCollapsed ? -1 : 0}
          aria-label="Close menu"
          className={`fixed inset-0 z-[45] bg-black/40 transition-opacity duration-300 ease-in-out ${
            isSidebarCollapsed
              ? "opacity-0 pointer-events-none"
              : "opacity-100"
          }`}
          onClick={() => onToggle(true)}
        />
      ) : null}

      <aside
        ref={sidebarRef}
        onMouseEnter={() => !isMobileTablet && setHoverStart(true)}
        onMouseLeave={() => !isMobileTablet && setHoverStart(false)}
        className={`fixed top-0 left-0 z-[70] h-full bg-main flex flex-col ${
          isMobileTablet
            ? `w-[240px] overflow-hidden transition-transform duration-300 ease-in-out ${
                isSidebarCollapsed
                  ? "-translate-x-full pointer-events-none"
                  : "translate-x-0"
              }`
            : `${isExpanded ? "w-[240px]" : "w-[72px]"} translate-x-0 transition-[width] duration-300 ease-in-out ${
                isSidebarCollapsed ? "menu-close" : ""
              }`
        }`}
      >
      {/* Header */}
      <div
        className={`flex items-center border-b-[0.5px] border-white/20 h-[56px] relative bg-main ${
          isMobileTablet || isExpanded
            ? "py-1.5 pl-2 pr-7"
            : "py-1.5 px-1.5 justify-center"
        }`}
      >
        <div
          className={`flex items-center w-full min-w-0 ${
            isMobileTablet || isExpanded ? "justify-start" : "justify-center"
          }`}
        >
          <AppBrandMark
            variant={
              isMobileTablet || isExpanded ? "sidebar" : "sidebar-collapsed"
            }
          />

          {!isMobileTablet ? (
            <button
              type="button"
              onClick={() => onToggle(!isSidebarCollapsed)}
              className="cursor-pointer absolute top-1/2 -translate-y-1/2 right-[-10px] z-[60]"
              aria-label={
                isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
              title={isSidebarCollapsed ? "Expand" : "Collapse"}
            >
              <span
                className={`inline-flex transition-transform duration-300 ease-in-out ${
                  isSidebarCollapsed ? "rotate-0" : "rotate-180"
                }`}
              >
                <CustomArrowIcon />
              </span>
            </button>
          ) : null}
        </div>
      </div>

      {/* Menu */}
      <div className="py-1.5 px-1.5 overflow-y-auto overflow-x-hidden h-[calc(100%_-_56px)]">
        <ul>
          {sideMenuList.map((item, index) => {
            const isSubItems = item?.subItems?.length > 0;
            const isActive =
              (location.pathname.startsWith(item?.path) ||
                path?.replace(/^\//, "") === item?.path?.replace(/^\//, "")) &&
              !isSubItems;
            const isExpanded = expandedMenus[index];

            // const hasPermission = true;
            const hasPermission = item?.permissions?.some((permission) =>
              permissionsForRole?.includes(permission),
            );

            if (!hasPermission) return null;

            const wrapperProps =
              item.subItems.length === 0
                ? {
                    to: item.path,
                    onClick: () => {
                      if (isMobileTablet) onToggle(true);
                    },
                  }
                : {
                    onClick: () => {
                      handleNavigation(item.path);
                      toggleSubMenu(index);
                    },
                  };

            const Wrapper = item.subItems.length === 0 ? Link : "a";

            return (
              <li key={index} className="cursor-pointer">
                <Wrapper
                  {...wrapperProps}
                  className={`flex items-center w-full rounded-[6px] px-2.5 py-[7px] mb-0.5 font-medium text-[14px] ${
                    isActive
                      ? "bg-white text-main"
                      : "bg-transparent text-white"
                  }`}
                >
                  {renderMenuItemContent(item, isActive, isExpanded)}
                </Wrapper>

                {/* Sub-Menu Items */}
                {isSubItems && isExpanded && (
                  <ul>
                    {item.subItems.map(
                      (subitem, subIndex) =>
                        permissionsForRole?.includes(subitem?.permission) && (
                          <li key={subIndex}>
                            <Link
                              to={subitem.path}
                              onClick={() => {
                                if (isMobileTablet) onToggle(true);
                              }}
                              className={`mb-0.5 flex items-start w-full rounded-[6px] font-medium pl-7 text-left pr-2 py-[7px] leading-[20px] text-[14px] cursor-pointer select-none ${
                                location.pathname.startsWith(subitem.path)
                                  ? "bg-white text-main"
                                  : "bg-transparent !text-white"
                              }`}
                            >
                              {isSidebarCollapsed && hoverStart && (
                                <>
                                  <span className="mr-[18px]">-</span>
                                  {subitem.name}
                                </>
                              )}
                              {!isSidebarCollapsed && (
                                <>
                                  <span className="mr-[18px]">-</span>
                                  {subitem.name}
                                </>
                              )}
                              {isSidebarCollapsed && !hoverStart && (
                                <span>-</span>
                              )}
                            </Link>
                          </li>
                        ),
                    )}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      </aside>
    </>
  );
};

export default Sidebar;
